/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
/**
 * MoviefoneParametersView — a form that lets users enter analysis parameters
 * and run the WASM analysis. All runAnalysisWasm parameters are visible as inputs.
 * The result is displayed as a collapsible JSON tree.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
  Input,
} from '@mui/material';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ChatMessage } from 'types/moviefone';
import { executeTool } from 'util/toolCalling';
import JsonTreeView from './JsonTreeView';
import {
  DefaultService as StreamAPI,
  MoviefoneAnalysis,
  ApiError,
  ErrorRsp,
  ChatCompletionChunk,
} from 'gen/stream-api/requests';

// ─── Constants ────────────────────────────────────────────────────

const DEFAULT_SYMBOL = 'SPX';
const DEFAULT_RISK_FREE_RATE = 0.043_3;

// ─── Component ────────────────────────────────────────────────────

interface MoviefoneParametersViewProps {
  initialSymbol?: string;
}

export default function MoviefoneParametersView({
  initialSymbol,
}: MoviefoneParametersViewProps) {
  const theme = useTheme();
  const [symbol, setSymbol] = useState(initialSymbol ?? DEFAULT_SYMBOL);
  const [riskFreeRate, setRiskFreeRate] = useState(DEFAULT_RISK_FREE_RATE);

  const [holdDays, setHoldDays] = useState(30);
  const [direction, setDirection] = useState(0); // 0=neutral, 1=bullish, 2=bearish
  const [futureSpot, setFutureSpot] = useState('');
  const [daysAhead, setDaysAhead] = useState('');
  const [loading, setLoading] = useState(false);
  const [moviefoneJson, setMoviefoneJson] = useState<MoviefoneAnalysis | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  const runAnalysis = useCallback(async () => {
    const sym = symbol.trim().toUpperCase();
    if (!sym) {
      setError('Symbol is required');
      return;
    }

    setLoading(true);
    setError(null);
    setMoviefoneJson(null);

    setStatus(`Fetching analysis ${sym}...`);

    try {
      const analysis = await StreamAPI.getV1MoviefoneAnalysis({
        sym,
        riskFreeRate,
        holdDays,
        direction,
        futureSpot: futureSpot ? parseFloat(futureSpot) : 0.0,
        daysAhead: daysAhead ? parseFloat(daysAhead) : 0.0,
      });

      console.log('[moviefone] runAnalysisWasm result:', analysis);
      setMoviefoneJson(analysis);
      setStatus('Done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  }, [symbol, riskFreeRate, holdDays, direction, futureSpot, daysAhead]);

  useEffect(() => {
    void runAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const submitChat = useCallback(async () => {
    setChatLoading(true);
    const userMsg: ChatMessage = {
      role: 'user',
      content: chatInput,
    };
    setChatMessages([...chatMessages, userMsg]);
    setChatInput('');

    await postChat({
      chatMessages,
      setChatMessages,
      moviefoneJson: moviefoneJson!,
      userMsg,
    });

    setChatLoading(false);
  }, [
    chatMessages,
    setChatMessages,
    chatInput,
    setChatInput,
    setChatLoading,
    moviefoneJson,
  ]);

  // ── Shared input style ──
  const inputSx = {
    width: 130,
    '& .MuiInputBase-root': {
      fontSize: '12px',
      fontFamily: '"SF Mono", "Fira Code", monospace',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flext-start',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* ── Parameter inputs (all runAnalysisWasm params) ── */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0,
          }}
        >
          {/* Row 1: Core params */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1, mt: 1 }}
          >
            <TextField
              label="Symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Risk-Free Rate"
              type="number"
              value={riskFreeRate}
              onChange={(e) => setRiskFreeRate(parseFloat(e.target.value) || 0)}
              size="small"
              inputProps={{ step: 0.001 }}
              sx={inputSx}
            />

            <TextField
              label="Hold Days"
              type="number"
              value={holdDays}
              onChange={(e) => setHoldDays(parseInt(e.target.value) || 5)}
              size="small"
              inputProps={{ min: 1 }}
              sx={inputSx}
            />
            <TextField
              label="Direction"
              select
              value={direction}
              onChange={(e) => setDirection(parseInt(e.target.value))}
              size="small"
              SelectProps={{ native: true }}
              sx={inputSx}
            >
              <option value={0}>Neutral</option>
              <option value={1}>Bullish</option>
              <option value={2}>Bearish</option>
            </TextField>
            <TextField
              label="Future Spot"
              type="number"
              value={futureSpot}
              onChange={(e) => setFutureSpot(e.target.value)}
              size="small"
              placeholder="(optional)"
              sx={inputSx}
            />
            <TextField
              label="Days Ahead"
              type="number"
              value={daysAhead}
              onChange={(e) => setDaysAhead(e.target.value)}
              size="small"
              placeholder="(optional)"
              sx={inputSx}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                void runAnalysis();
              }}
              disabled={loading || !symbol.trim()}
              startIcon={
                loading ? <CircularProgress size={14} /> : <PlayArrowIcon />
              }
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 120,
              }}
            >
              {loading ? 'Running...' : 'Run Analysis'}
            </Button>
            {status != null && status !== '' && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '11px',
                  ml: 'auto',
                  paddingLeft: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                {status}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* ── Error ── */}
        {error != null && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'rgba(244,67,54,0.08)',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* ── JSON tree + text_output ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 2,
          }}
        >
          {moviefoneJson != null ? (
            <>
              <JsonTreeView
                data={(() => {
                  const {
                    dollar_value_contracts: _dvc,
                    scenario_pricing: _sp,
                    ...rest
                  } = moviefoneJson;
                  return rest;
                })()}
              />
              {(() => {
                // const typedResult = moviefoneJson;
                return true /*typedResult.text_output != null &&
                  typedResult.text_output !== ''*/;
              })() && (
                <Box sx={{ mt: 2 }}>
                  <pre
                    style={{
                      margin: 0,
                      fontFamily:
                        '"SF Mono", "Fira Code", "Consolas", monospace',
                      fontSize: '12px',
                      lineHeight: '17px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.default,
                      padding: '12px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {'moviefoneJson.text_output'}
                  </pre>
                </Box>
              )}
            </>
          ) : (
            <Stack
              sx={{
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                {loading
                  ? 'Analyzing...'
                  : 'Enter a symbol and click Run Analysis'}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
      <Box sx={{ flexGrow: '1', maxWidth: '50%' }}>
        <Box
          sx={{
            display: 'flex',
            direction: 'row',
            alignItems: 'flex-start',
            ml: 5,
            mt: 4,
          }}
        >
          <Input
            sx={{ flexGrow: 1 }}
            placeholder="Chat Input"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            disabled={chatLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void submitChat();
              }
            }}
          />
          <Button onClick={() => void submitChat()} disabled={chatLoading}>
            Submit
          </Button>
        </Box>
        <JsonTreeView data={chatMessages} childrenDefaultOpen={true} />
        {chatLoading && (
          <CircularProgress size={14} sx={{ marginLeft: 6, marginTop: 6 }} />
        )}
      </Box>
    </Box>
  );
}

interface PostChatParams {
  chatMessages: ChatMessage[];
  setChatMessages: (v: ChatMessage[]) => void;
  moviefoneJson: MoviefoneAnalysis;
  userMsg?: ChatMessage;
}

const postChat = async ({
  chatMessages,
  setChatMessages,
  moviefoneJson,
  userMsg,
}: PostChatParams) => {
  try {
    const rsp = (await StreamAPI.postV1MoviefoneChat({
      requestBody: {
        conversation: chatMessages,
        moviefone_json: moviefoneJson as unknown as MoviefoneAnalysis,
        user_message: userMsg?.content,
      },
    })) as string;
    let content = '';
    const tool_calls = [];
    for (const chunk of rsp.split(/\n\n/g)) {
      const data = chunk.replace(/^data: /, '');
      if (data != '[DONE]' && data != '') {
        const json = JSON.parse(data) as ChatCompletionChunk;
        for (const c of json.choices) {
          if (c.delta.content != null) {
            content += c.delta.content;
          }

          tool_calls.push(...(c.delta?.tool_calls ?? []));
        }
      }
    }

    const responseMessage = {
      content,
      role: 'assistant',
    } as ChatMessage;

    setChatMessages([
      ...chatMessages,
      ...(userMsg != null ? [userMsg] : []),
      responseMessage,
    ]);

    if (tool_calls.length > 0) {
      const updatedChatMessages: ChatMessage[] = [
        ...chatMessages,
        ...(userMsg != null ? [userMsg] : []),
        responseMessage,
        ...(await Promise.all(
          tool_calls.map(
            async (tc) =>
              await executeTool(tc.function!.name!, {}, moviefoneJson).then(
                (result) =>
                  ({
                    role: 'tool',
                    content: JSON.stringify(result),
                    tool_call_id: tc.id,
                  }) as ChatMessage,
              ),
          ),
        )),
      ];
      setChatMessages(updatedChatMessages);
      await postChat({
        chatMessages: updatedChatMessages,
        setChatMessages,
        moviefoneJson,
      });
    }
  } catch (e) {
    if (e instanceof ApiError) {
      const errRsp = e.body as ErrorRsp;
      setChatMessages([
        ...chatMessages,
        ...(userMsg != null ? [userMsg] : []),
        {
          content: errRsp?.msg,
          role: 'assistant',
        } as ChatMessage,
      ]);
      console.warn(errRsp?.msg);
    } else {
      console.error((e as Error)?.message ?? e);
    }
  }
};
